import {
  Package,
  Warehouse,
  ShoppingCart,
  AlertTriangle,
  TrendingUp,
  Truck,
  CheckCircle2,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

interface DeliveryItem {
  readonly day: string;
  readonly supplier: string;
  readonly items: number;
  readonly status: 'delivered' | 'in-transit' | 'scheduled';
}

const foodCostData = [
  {
    month: 'Aug',
    value: 32.1 
  },
  {
    month: 'Sep',
    value: 31.4 
  },
  {
    month: 'Oct',
    value: 33.2 
  },
  {
    month: 'Nov',
    value: 30.8 
  },
  {
    month: 'Dec',
    value: 29.5 
  },
  {
    month: 'Jan',
    value: 28.7 
  },
];

const deliveriesData: readonly DeliveryItem[] = [
  {
    day: 'Mon',
    supplier: 'Sysco',
    items: 47,
    status: 'delivered' 
  },
  {
    day: 'Tue',
    supplier: 'US Foods',
    items: 32,
    status: 'delivered' 
  },
  {
    day: 'Wed',
    supplier: 'Local Farms',
    items: 18,
    status: 'in-transit' 
  },
  {
    day: 'Thu',
    supplier: 'Seafood Direct',
    items: 24,
    status: 'scheduled' 
  },
  {
    day: 'Fri',
    supplier: 'Sysco',
    items: 56,
    status: 'scheduled' 
  },
];

export function DashboardPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-muted-foreground">Welcome back. Here is your operations overview.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Total SKUs"
          value="12,847"
          change="+124 this month"
          icon={Package}
        />
        <KpiCard
          title="Inventory Value"
          value="$2.4M"
          change="+8.2% vs last month"
          icon={Warehouse}
        />
        <KpiCard
          title="Open POs"
          value="47"
          change="$340K pending"
          icon={ShoppingCart}
        />
        <KpiCard
          title="Expiring Soon"
          value="23"
          change="Items within 7 days"
          icon={AlertTriangle}
          variant="warning"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Food Cost Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FoodCostChart data={foodCostData} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Deliveries This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DeliveriesSchedule data={deliveriesData} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <ActivityItem
              title="PO #4521 received"
              description="Fresh produce from Sysco - 47 items"
              time="2 hours ago"
            />
            <ActivityItem
              title="Requisition approved"
              description="Main Dining - 23 items for dinner service"
              time="4 hours ago"
            />
            <ActivityItem
              title="Low stock alert"
              description="Heavy Cream 1L below par level"
              time="5 hours ago"
            />
            <ActivityItem
              title="New recipe added"
              description="Lobster Thermidor - by Chef Martinez"
              time="Yesterday"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface KpiCardProps {
  readonly title: string;
  readonly value: string;
  readonly change: string;
  readonly icon: LucideIcon;
  readonly variant?: 'default' | 'warning';
}

function KpiCard({
  title,
  value,
  change,
  icon,
  variant = 'default',
}: KpiCardProps) {
  const IconComponent = icon;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <IconComponent className={`h-5 w-5 ${variant === 'warning' ? 'text-amber-500' : 'text-muted-foreground'}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{change}</p>
      </CardContent>
    </Card>
  );
}

interface ActivityItemProps {
  readonly title: string;
  readonly description: string;
  readonly time: string;
}

function ActivityItem({
  title,
  description,
  time,
}: ActivityItemProps) {
  return (
    <div className="flex items-start justify-between border-b pb-4 last:border-0 last:pb-0">
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <span className="text-xs text-muted-foreground">{time}</span>
    </div>
  );
}

interface FoodCostDataPoint {
  readonly month: string;
  readonly value: number;
}

interface FoodCostChartProps {readonly data: readonly FoodCostDataPoint[];}

function FoodCostChart({ data }: FoodCostChartProps) {
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue;
  const chartHeight = 160;
  const padding = 10;

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = chartHeight - padding - ((d.value - minValue) / range) * (chartHeight - padding * 2);
    return {
      x,
      y,
      ...d 
    };
  });

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ');

  const areaD = `${pathD} L 100 ${chartHeight - padding} L 0 ${chartHeight - padding} Z`;

  const latestValue = data[data.length - 1].value;
  const previousValue = data[data.length - 2].value;
  const trend = latestValue < previousValue ? 'down' : 'up';

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <span className="text-3xl font-bold">{latestValue}%</span>
          <span className={`ml-2 text-sm ${trend === 'down' ? 'text-green-600' : 'text-red-500'}`}>
            {trend === 'down' ? '↓' : '↑'} {Math.abs(latestValue - previousValue).toFixed(1)}%
          </span>
        </div>
        <span className="text-sm text-muted-foreground">Target: 30%</span>
      </div>
      
      <div className="relative h-40">
        <svg viewBox={`0 0 100 ${chartHeight}`} className="h-full w-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="foodCostGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <path d={areaD} fill="url(#foodCostGradient)" />
          <path d={pathD} fill="none" stroke="rgb(59, 130, 246)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          {points.map((p) => (
            <circle key={p.month} cx={p.x} cy={p.y} r="3" fill="rgb(59, 130, 246)" vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
      </div>
      
      <div className="flex justify-between text-xs text-muted-foreground">
        {data.map((d) => (
          <span key={d.month}>{d.month}</span>
        ))}
      </div>
    </div>
  );
}

interface DeliveriesScheduleProps {readonly data: readonly DeliveryItem[];}

function DeliveriesSchedule({ data }: DeliveriesScheduleProps) {
  const statusConfig = {
    delivered: {
      icon: CheckCircle2,
      color: 'text-green-600',
      bg: 'bg-green-50',
      label: 'Delivered' 
    },
    'in-transit': {
      icon: Truck,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      label: 'In Transit' 
    },
    scheduled: {
      icon: Clock,
      color: 'text-gray-500',
      bg: 'bg-gray-50',
      label: 'Scheduled' 
    },
  };

  return (
    <div className="space-y-3">
      {data.map((delivery) => {
        const config = statusConfig[delivery.status];
        const StatusIcon = config.icon;
        return (
          <div key={`${delivery.day}-${delivery.supplier}`} className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full ${config.bg}`}>
                <StatusIcon className={`h-5 w-5 ${config.color}`} />
              </div>
              <div>
                <p className="font-medium">{delivery.supplier}</p>
                <p className="text-sm text-muted-foreground">{delivery.items} items</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">{delivery.day}</p>
              <p className={`text-xs ${config.color}`}>{config.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
